import { LazyVideo } from "@/components/ui/lazy-video";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { ReactNode } from "react";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";

interface PageHeroProps {
  title: ReactNode;
  subtitle?: ReactNode;
  videoSrc?: string;
  imageSrc?: string;
  /** Poster image shown before/instead of the video (mobile performance fallback) */
  posterSrc?: string;
  imageAlt?: string;
  /** Muted band when no media is provided */
  variant?: "media" | "muted";
  eyebrow?: ReactNode;
}

export function PageHero({
  title,
  subtitle,
  videoSrc,
  imageSrc,
  posterSrc,
  imageAlt,
  variant = videoSrc || imageSrc ? "media" : "muted",
  eyebrow,
}: PageHeroProps) {
  if (variant === "muted") {
    return (
      <section data-page-hero className="page-hero-muted relative overflow-hidden py-16 md:py-32 bg-[linear-gradient(158deg,#F7EDDB_0%,#F0E4CE_100%)] border-b border-sand-deep">
        <div className="absolute right-[-120px] top-[-140px] w-[440px] h-[440px] rounded-full bg-[radial-gradient(circle,rgba(47,94,52,0.14)_0%,rgba(47,94,52,0)_70%)] pointer-events-none" />
        <div className="container mx-auto px-6 text-center relative z-10">
          {eyebrow && (
            <span className="inline-block text-driftwood font-sans font-normal text-[11px] tracking-[0.3em] uppercase mb-5">
              {eyebrow}
            </span>
          )}
          <h1 className="font-serif font-normal text-forest tracking-tight leading-[1] md:leading-[0.95] text-[clamp(34px,8vw,84px)] mb-6">
            {title}
          </h1>
          {subtitle && (
            <p className="text-[17px] font-sans text-ink-soft max-w-[660px] mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section data-hero-header className="page-media-hero relative overflow-hidden bg-ink">
      <div className="absolute inset-0 z-0">
        {videoSrc ? (
          <LazyVideo
            src={videoSrc}
            poster={posterSrc ? optimizedUrl(posterSrc, 1920, 90) : posterSrc}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ aspectRatio: "16/9" }}
            aria-label={imageAlt || undefined}
          />
        ) : imageSrc ? (
          <img
            src={optimizedUrl(imageSrc, 1600)}
            srcSet={optimizedSrcSet(imageSrc, [768, 1200, 1600, 1920])}
            sizes="100vw"
            alt={imageAlt || ""}
            className="w-full h-full object-cover"
            decoding="async"
            fetchPriority="high"
          />
        ) : null}
        {/* Scrim matching the brand kit for yellow text readability */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_65%,rgba(20,20,16,0.15)_100%)]" />
      </div>
      
      <div className="container mx-auto px-6 text-center relative z-10 flex flex-col items-center justify-end min-h-0 h-full">
        <ScrollReveal className="media-copy-panel px-6 py-7 md:px-10">
          {eyebrow && (
            <span className="wordmark-gold inline-block font-sans font-normal text-[11px] tracking-[0.3em] uppercase mb-5">
              {eyebrow}
            </span>
          )}
          <h1 className="wordmark-gold font-serif font-[500] leading-[0.95] text-[clamp(40px,7.5vw,92px)] tracking-[0.01em]">
            {title}
          </h1>
          <div className="flex items-center gap-3 mt-[18px] mb-[16px] w-full max-w-[min(80vw,440px)] mx-auto">
            <span className="h-px flex-1 bg-[#E3CF98]/55" />
            <span className="w-[7px] h-[7px] rotate-45 bg-[#E3CF98]" />
            <span className="h-px flex-1 bg-[#E3CF98]/55" />
          </div>
          {subtitle && (
            <p className="wordmark-gold font-serif italic text-[clamp(16px,2.6vw,22px)] max-w-[640px] mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </ScrollReveal>
      </div>
    </section>
  );
}
